/**
 * Client-Side Files API
 *
 * This module provides client-side API functions for file operations
 * to be used with React Query.
 */

import { directApiClient } from '@/lib/api/direct-client';
import { uploadFileToUrl } from '@/lib/uploads';
import { buildUploadCompleteNaming } from './upload-naming';
import type {
  MatterFile,
  FileListResponse,
  FileFilters,
  UpdateFileRequest,
  GetPresignedUrlRequest,
  PresignedUrlResponse,
  UploadCompleteRequest,
  FileType,
  CoreSlotFileType,
  IdentitySlotFileType
} from './types';
import type { UploadCompleteResponse } from '@/lib/services/general-files/types';

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all files for a matter
 */
export async function getFiles(
  matterId: number,
  filters?: FileFilters
): Promise<FileListResponse> {
  // Map frontend filter names to backend query param names
  const params: Record<string, unknown> = { ...filters };
  if (params.fileType) {
    params.type = params.fileType;
    delete params.fileType;
  }

  const files = await directApiClient.get<MatterFile[]>(
    `/matters/${matterId}/files`,
    { params }
  );

  return {
    files: files || [],
    total: files?.length || 0
  };
}

/**
 * Get a specific file
 */
export async function getFile(
  matterId: number,
  fileId: number
): Promise<MatterFile> {
  return directApiClient.get<MatterFile>(`/files/${fileId}`);
}

/**
 * Delete a file
 */
export async function deleteFile(
  matterId: number,
  fileId: number
): Promise<void> {
  return directApiClient.delete(`/files/${fileId}`);
}

/**
 * Update file metadata
 */
export async function updateFile(
  matterId: number,
  fileId: number,
  data: UpdateFileRequest
): Promise<MatterFile> {
  return directApiClient.put<MatterFile>(`/files/${fileId}`, data);
}

/**
 * Uncategorise a file from a core document slot, moving it back into Additional
 * Documents. Clears the matter slot reference + AI-extracted mirror pointer and
 * retypes the file to additional-document (server-side). Grant lodgement flags
 * and references are left untouched.
 */
export async function uncategoriseFile(
  matterId: number,
  fileId: number
): Promise<MatterFile> {
  return directApiClient.post<MatterFile>(
    `/matters/${matterId}/files/${fileId}/uncategorise`,
    {}
  );
}

/**
 * Mark a file as one of the matter's core documents (VALE-806) — the inverse of
 * `uncategoriseFile`. Whatever already occupies the target slot is moved back into
 * Additional Documents server-side, in the same transaction, so exactly one live
 * file carries each core type.
 */
export async function categoriseFile(
  matterId: number,
  fileId: number,
  fileType: CoreSlotFileType
): Promise<MatterFile> {
  return directApiClient.post<MatterFile>(
    `/matters/${matterId}/files/${fileId}/categorise`,
    { fileType }
  );
}

/**
 * Point a contact's primary/secondary identification slot at this file (VALE-806).
 * The contact's incumbent identity document is moved back into Additional Documents,
 * and the file is released from whichever contact previously claimed it.
 */
export async function assignIdentityDocument(
  matterId: number,
  fileId: number,
  contactId: number,
  fileType: IdentitySlotFileType
): Promise<MatterFile> {
  return directApiClient.post<MatterFile>(
    `/matters/${matterId}/files/${fileId}/assign-identity`,
    { contactId, fileType }
  );
}

/**
 * Get presigned URL for file upload
 */
export async function getPresignedUrl(
  data: GetPresignedUrlRequest
): Promise<PresignedUrlResponse> {
  return directApiClient.post<PresignedUrlResponse>(
    '/files/pre-signed-url',
    data
  );
}

/**
 * Complete file upload after S3 upload
 */
export async function uploadComplete(
  data: UploadCompleteRequest
): Promise<UploadCompleteResponse> {
  // The endpoint answers `{ fileId }`, not the file row. Typed as `MatterFile`
  // it let every caller read `.id`, which is undefined: attaching a client's
  // prior will and filing a signed original both failed on it (VALE-1892).
  return directApiClient.post<UploadCompleteResponse>(
    `/files/${data.fileId}/upload-complete`,
    {
      originalFilename: data.originalFilename,
      ...(data.displayName && { displayName: data.displayName })
    }
  );
}

/**
 * Combined file upload (presigned URL + upload + complete)
 * This is a convenience function for uploading files
 */
export async function uploadFile(
  matterId: number,
  file: File,
  fileType: string = 'additional-document',
  options?: {
    isCertifiedCopy?: boolean;
    displayName?: string;
    contactId?: number;
    institutionId?: number;
    institutionFormId?: number;
    signatoryContactId?: number;
    metadata?: Record<string, unknown>;
    /** Real S3-PUT progress, 0–100. Routed through the shared upload primitive. */
    onProgress?: (percent: number) => void;
    /** Cancel the in-flight PUT. Aborting rejects with an `AbortError`. */
    signal?: AbortSignal;
  }
): Promise<UploadCompleteResponse> {
  // Step 1: Get presigned URL
  // For institution forms, include the fields in metadata
  const presignedMetadata: Record<string, unknown> = {
    ...(options?.metadata || {})
  };

  // Ensure institution fields are integers for backend validation
  if (options?.institutionId !== undefined) {
    presignedMetadata.institutionId = Number(options.institutionId);
  }
  if (options?.institutionFormId !== undefined) {
    presignedMetadata.institutionFormId = Number(options.institutionFormId);
  }
  if (options?.signatoryContactId !== undefined) {
    presignedMetadata.signatoryContactId = Number(options.signatoryContactId);
  }

  const presignedResponse = await getPresignedUrl({
    matterId,
    fileType: fileType as FileType,
    isCertifiedCopy: options?.isCertifiedCopy,
    contactId: options?.contactId,
    ...(Object.keys(presignedMetadata).length > 0 && {
      metadata: presignedMetadata
    })
  });

  // Step 2: Upload to S3 (only for real API) — via the shared progress-capable
  // primitive (LF-4771), the single home for the S3 PUT.
  await uploadFileToUrl(presignedResponse.presignedUrl, file, {
    contentType: file.type,
    onProgress: (p) => options?.onProgress?.(p.percent),
    signal: options?.signal
  });

  // Step 3: Complete upload. `buildUploadCompleteNaming` owns the
  // filename-vs-label rule for both upload paths — see upload-naming.ts.
  return uploadComplete({
    fileId: presignedResponse.fileId,
    ...buildUploadCompleteNaming(file.name, options?.displayName)
  });
}

// ============================================================================
// EXPORT API OBJECT
// ============================================================================

export const filesApi = {
  getFiles,
  getFile,
  deleteFile,
  updateFile,
  uncategoriseFile,
  categoriseFile,
  assignIdentityDocument,
  getPresignedUrl,
  uploadComplete,
  uploadFile
};
